// キャンバスの基準寸法（仕様で固定）。保存境界と同じ値を domain から共有する。
export { CANVAS_WIDTH_PX, CANVAS_HEIGHT_PX } from "@refinement-canvas/domain";

// グリッドの間隔（薄い点線）。ASWD 移動の体感速度向上のため細かめに表示する。
export const CANVAS_GRID_PX = 20;
// グリッド吸着モードのスナップ単位。背面に表示しているグリッドドットへ必ず吸着するよう、
// 表示ドット間隔（CANVAS_GRID_PX）と同一値にする。
export const CANVAS_SNAP_PX = CANVAS_GRID_PX;

// ズームの上下限
// 最大ズームインで 640x360 の領域が画面いっぱいに表示される（10240px 幅の 1/16）
// 最大ズームアウトで 10240x5760 全体が画面に収まる（実際の倍率はビューポートサイズで決まる）
export const ZOOM_MIN = 0.05; // 1/20。最小ズームの目安
export const ZOOM_MAX = 16; // 10240 / 640 の比率上限

// 段階ズーム（Ctrl + ホイール / Ctrl + プラスマイナス / +- ボタン）
export const ZOOM_STEPS: readonly number[] = [
  0.0625, 0.083, 0.125, 0.167, 0.25, 0.333, 0.5, 0.667, 1, 1.5, 2, 3, 4, 6, 8, 12, 16,
];

export interface Point2D {
  readonly x: number;
  readonly y: number;
}

export interface ViewState {
  readonly panX: number; // ステージ左上の screen 座標 (px)
  readonly panY: number;
  readonly zoom: number; // 0..ZOOM_MAX
}

/**
 * screen 座標 (clientX, clientY) を stage 座標へ変換する。
 */
export function screenToStage(point: Point2D, view: ViewState): Point2D {
  return {
    x: (point.x - view.panX) / view.zoom,
    y: (point.y - view.panY) / view.zoom,
  };
}

/**
 * stage 座標を screen 座標へ変換する。
 */
export function stageToScreen(point: Point2D, view: ViewState): Point2D {
  return {
    x: point.x * view.zoom + view.panX,
    y: point.y * view.zoom + view.panY,
  };
}

/**
 * 指定 stage 点を中心に倍率を変更する。マウス位置を中心とするズームに使用。
 */
export function zoomAt(view: ViewState, screenPoint: Point2D, nextZoom: number): ViewState {
  const stagePoint = screenToStage(screenPoint, view);
  const clampedZoom = Math.min(Math.max(nextZoom, ZOOM_MIN), ZOOM_MAX);
  return {
    zoom: clampedZoom,
    panX: screenPoint.x - stagePoint.x * clampedZoom,
    panY: screenPoint.y - stagePoint.y * clampedZoom,
  };
}

/**
 * 段階ズーム（一段拡大）を返す。
 */
export function stepZoomIn(currentZoom: number): number {
  const next = ZOOM_STEPS.find((z) => z > currentZoom + 1e-6);
  return next ?? ZOOM_MAX;
}

/**
 * 段階ズーム（一段縮小）を返す。
 */
export function stepZoomOut(currentZoom: number): number {
  const reversed = [...ZOOM_STEPS].reverse();
  const prev = reversed.find((z) => z < currentZoom - 1e-6);
  return prev ?? ZOOM_MIN;
}

/**
 * ホイールの deltaY 方向に応じて段階ズーム（拡大/縮小）を 1 段適用し、screenPoint を中心に保った
 * 次の ViewState を返す。Ctrl + ホイールのズーム経路（キャンバス本体・コメント UI 上の双方）で
 * 同一挙動にするための共通ヘルパー。deltaY < 0 で拡大、それ以外で縮小。
 */
export function applyStepZoom(view: ViewState, screenPoint: Point2D, deltaY: number): ViewState {
  const nextZoom = deltaY < 0 ? stepZoomIn(view.zoom) : stepZoomOut(view.zoom);
  return zoomAt(view, screenPoint, nextZoom);
}

// wheel イベントの deltaMode: ピクセル単位を表す値（WheelEvent.DOM_DELTA_PIXEL）。
const WHEEL_DELTA_MODE_PIXEL = 0;

// ノッチのあるマウスホイールを見分けるための、レガシー値 wheelDeltaY と deltaY の比。
// Chromium 系は wheelDeltaY = ホイールの刻み数 * 120 として通知するため、1 ノッチが
// deltaY=100 / wheelDeltaY=120（比 1.2）になる。一方トラックパッド由来の wheel は
// 1 刻みを 40px 相当として扱うため比が 3 になる。比は delta の大きさに依存しないので、
// 環境によって deltaY の絶対値が変わっても誤判定しない。
//
// 既知の割り切り: 比が 1.2 から外れるマウス（高分解能ホイールや、1 ノッチあたりの
// スクロール量を変える一部の環境）は無段階ズーム側になる。ピンチをマウスと誤判定すると
// 無段階ズームが一切効かず「指を少し動かしただけで倍率が数段飛ぶ」実害が出るのに対し、
// 逆方向の誤判定はホイールのズームが滑らかになるだけで実害が小さいため、この向きを選ぶ。
const MOUSE_WHEEL_DELTA_RATIO = 1.2;
// 比較の許容誤差（表示倍率などで delta がわずかに丸められる場合に備える）。
const MOUSE_WHEEL_DELTA_RATIO_TOLERANCE = 0.05;

// wheelDeltaY を提供しないブラウザ向けの代替判定に使う閾値。マウスホイールの 1 ノッチは
// ピクセル単位で 100 前後になるため、それ未満の細かい deltaY をピンチ候補とみなす。
const PINCH_WHEEL_DELTA_MAX = 50;

// ピンチ 1 イベントあたりのズーム倍率 = exp(-deltaY * PINCH_ZOOM_SENSITIVITY)。
// ブラウザは指の間隔の変化率 s（例: 2% 広げたなら 1.02）を deltaY ≒ -(s - 1) * 100 として通知する
// 想定だが、実際に 1 イベントで届く deltaY の大きさは OS やドライバによって桁が変わる。
// そのため係数を小さめに取ったうえで、下の上限で 1 イベントあたりの変化量を頭打ちにする。
const PINCH_ZOOM_SENSITIVITY = 0.004;

// ピンチ 1 イベントで許容するズーム倍率の指数の上限（exp(0.04) ≒ 1 イベント 4% まで）。
// deltaY の桁が大きい環境でも「指を少し動かしただけで一気に拡大／縮小する」ことを防ぎ、
// 変化量を環境に依存させないための頭打ちとして使う。
// 感度と上限は必ず同じ倍率で調整する。deltaY が小さい環境では感度が、大きい環境では上限が
// 効くため、片方だけを変えると環境によって操作感の変化量がそろわなくなる。
const PINCH_ZOOM_MAX_EXPONENT_PER_EVENT = 0.04;

/**
 * ズーム判定に必要な wheel イベントの値だけを受け取るための型。
 * DOM 型へ依存させないことで、純粋関数として単体テストできるようにする。
 */
export interface WheelZoomInput {
  readonly deltaY: number;
  readonly deltaMode: number;
  /**
   * WheelEvent.wheelDeltaY（非標準の旧 API）。deltaY との比でノッチのあるマウスホイールを
   * 見分けるために使う。提供しないブラウザでは undefined になる。
   */
  readonly wheelDeltaY?: number | undefined;
}

/**
 * トラックパッドの 2 本指ピンチ由来の wheel イベントかどうかを判定する。
 * true ならピンチ（無段階ズーム）、false ならマウスホイール（段階ズーム）として扱う。
 *
 * 判定は「ノッチのあるマウスホイールだと積極的に確認できた場合だけ false」とする。
 * deltaY の絶対値はトラックパッドでも OS やドライバによって 1 桁から 100 前後まで幅があり、
 * 大きさだけで見分けようとするとピンチをマウス扱いしてしまい、無段階ズームが一度も
 * 効かなくなるため、比による判定を主軸に置いている。
 */
export function isTrackpadPinchWheel(wheel: WheelZoomInput): boolean {
  // 行 / ページ単位で通知されるのはノッチのあるマウスホイール。
  if (wheel.deltaMode !== WHEEL_DELTA_MODE_PIXEL) return false;
  const notch = wheel.wheelDeltaY;
  if (
    typeof notch === "number" &&
    Number.isFinite(notch) &&
    notch !== 0 &&
    wheel.deltaY !== 0
  ) {
    // wheelDeltaY が使える環境では比で判定する（マウス = 1.2 / トラックパッド = 3）。
    const ratio = Math.abs(notch / wheel.deltaY);
    return Math.abs(ratio - MOUSE_WHEEL_DELTA_RATIO) > MOUSE_WHEEL_DELTA_RATIO_TOLERANCE;
  }
  // wheelDeltaY を提供しないブラウザでは、1 ノッチ未満の細かい deltaY をピンチとみなす。
  return Math.abs(wheel.deltaY) < PINCH_WHEEL_DELTA_MAX;
}

/**
 * トラックパッドのピンチ用の無段階ズーム。deltaY の大きさに比例した倍率を現在のズームへ掛け、
 * screenPoint が指す stage 点を保ったままの次の ViewState を返す。deltaY < 0 で拡大、> 0 で縮小。
 * 倍率は指数で計算するため、拡大と縮小が同じ指移動量で対称になる。
 * 1 イベントあたりの変化量には上限を設け、deltaY の桁が大きい環境でも急変しないようにする。
 */
export function applyPinchZoom(view: ViewState, screenPoint: Point2D, deltaY: number): ViewState {
  // ピンチ開始直後などに届く deltaY=0 では view を作り直さない。作り直すと表示が変わらないのに
  // 再レンダー・表示位置の保存・URL 同期が走り、zoomAt の除算/乗算で pan に丸め誤差も乗るため。
  if (deltaY === 0) return view;
  const rawExponent = -deltaY * PINCH_ZOOM_SENSITIVITY;
  const exponent = Math.min(
    Math.max(rawExponent, -PINCH_ZOOM_MAX_EXPONENT_PER_EVENT),
    PINCH_ZOOM_MAX_EXPONENT_PER_EVENT
  );
  return zoomAt(view, screenPoint, view.zoom * Math.exp(exponent));
}

/**
 * Ctrl(⌘) + wheel によるズームを、入力デバイスに応じて振り分ける共通ヘルパー。
 * - トラックパッドの 2 本指ピンチ: 無段階ズーム（applyPinchZoom）
 * - マウスホイール: 従来どおりの段階ズーム（applyStepZoom）
 * ホイールによるズーム経路（キャンバス本体・コメント UI・各 UI パネル）で挙動を揃えるために使う。
 */
export function applyWheelZoom(
  view: ViewState,
  screenPoint: Point2D,
  wheel: WheelZoomInput
): ViewState {
  return isTrackpadPinchWheel(wheel)
    ? applyPinchZoom(view, screenPoint, wheel.deltaY)
    : applyStepZoom(view, screenPoint, wheel.deltaY);
}
